import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, of } from 'rxjs';

import { catchError, map } from 'rxjs/operators';
import {
  EditingTreatmentApi,
  FertilizerReportItemApi,
  RecommendedNutrientQuantityDataWithIds,
} from 'src/app/advice/models/fertilization-result.model';

import { CopyDefaultAdvicesResponse } from 'src/app/overview/models/cop-default-advice-response.model';
import { TreatmentType } from 'src/app/sales-order-period/models/sales-order-period.models';
import { Environment } from 'src/environments/environment';

import { SoilAnalysisDesignations } from '../../soil-analysis/models/soil-analysis.model';
import {
  CropData,
  CropFieldAdvice,
  CustomerFertilizerStock,
  Employee,
  ExecutedTreatment,
  ExecutedTreatmentWithNitrogenEffectivity,
  FertilizationBalanceSettingsResponse,
  FertilizationSettingWithName,
  NitrogenCalculationSettingToggles,
  SamplesBy,
  SoilAnalysis,
  SpecificReferenceBlock,
  Template,
  Treatment,
  UpdateGrasslandUsageRequest,
  UpsertFertilizationSettingRequest,
} from '../models';
import { UrlHelpers } from './urlHelpers';

const soilAnalysisMaxYears = 5;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  // change the base url to point to the according service name
  protected baseUrl = this.env.FIELD_ADVICE_REBUILD_DONE
    ? this.env.APIM_BASE_URL + '/arable-service/advice/'
    : this.env.API_BASE_URL + '/service/field-advice/secure/';
  protected baseUrlAdmin = this.env.FIELD_ADVICE_TEMPLATE_REBUILD_DONE
    ? this.env.APIM_BASE_URL + '/arable-service/fieldadvice/'
    : this.env.API_BASE_URL + '/service/field-advice-admin/secure/';
  protected baseUrlSoilAnalysis = this.env.APIM_BASE_URL + '/arable-service/fieldregistration/';
  protected baseUrlFieldReport = this.env.USE_ARABLE_SERVICE_FIELD_REPORT_ROUTES
    ? `${this.env.APIM_BASE_URL}/arable-service/advice/`
    : `${this.env.API_BASE_URL}/service/field-report/secure/`;
  protected baseUrlArableAdvice = this.env.APIM_BASE_URL + '/arable-advice-service/';

  // add methods to post, put, get or delete from this api here
  // api files can be split up and should then be named according to the service they are calling
  // for instance, admin-authorization-api.service.ts, AdminAuthorizationApiService

  getCropFieldAdvicesByCropYear(cropYear: number): Observable<CropFieldAdvice[]> {
    return this.http
      .get<{
        cropFieldAdvices: CropFieldAdvice[];
      }>(`${this.baseUrl}cropfieldadvice/cropyear/${cropYear}`)
      .pipe(map((data) => data.cropFieldAdvices));
  }

  copyDefaultCropCareAdvices(customerId: number, year: number) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http
        .post<CopyDefaultAdvicesResponse>(`${this.baseUrl}copy-default-gbm-treatments`, null, {
          params: { customerId: customerId.toString(), cropYear: year.toString() },
        })
        .pipe(map((response) => response));
    }
    return this.http
      .post<CopyDefaultAdvicesResponse>(`${this.baseUrl}advice/copy-default-gbm-treatments`, null, {
        params: { customerId: customerId.toString(), cropYear: year.toString() },
      })
      .pipe(map((response) => response));
  }

  copyDefaultFertilizationAdvices(customerId: number, year: number) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http
        .post<CopyDefaultAdvicesResponse>(
          `${this.baseUrl}copy-default-fertilizer-treatments`,
          null,
          {
            params: { customerId: customerId.toString(), cropYear: year.toString() },
          },
        )
        .pipe(map((response) => response));
    }
    return this.http
      .post<CopyDefaultAdvicesResponse>(
        `${this.baseUrl}advice/copy-default-fertilizer-treatments`,
        null,
        { params: { customerId: customerId.toString(), cropYear: year.toString() } },
      )
      .pipe(map((response) => response));
  }

  getLatestSoilAnalysis(soilAnalysisIds: number[]): Observable<SoilAnalysis> {
    const soilAnalysisIdsParams = UrlHelpers.getParamString('soilAnalysisIds', soilAnalysisIds);
    return this.http.get<SoilAnalysis>(
      `${
        this.baseUrlSoilAnalysis
      }soilanalysis/mostrecentbyproperties?oneDesignationSuffices=true&maxYears=${soilAnalysisMaxYears}&${UrlHelpers.getParamString(
        'designationNames',
        [
          SoilAnalysisDesignations.pAl,
          SoilAnalysisDesignations.pPae,
          SoilAnalysisDesignations.pw,
          SoilAnalysisDesignations.capae,
          SoilAnalysisDesignations.pHKCl,
        ],
      )}${soilAnalysisIdsParams ? '&' + soilAnalysisIdsParams : ''}`,
    );
  }

  getTemplate(
    cropYear: number,
    cropFieldAdvice: CropFieldAdvice,
    treatmentType: TreatmentType,
  ): Observable<Template> {
    let params = new HttpParams()
      .set('CropYear', cropYear.toString())
      .set('CropId', cropFieldAdvice.crop.id.toString())
      .set('SoilTypeId', cropFieldAdvice.soilType.id.toString())
      .set('IsSkal', cropFieldAdvice.isSkal.toString())
      .set('IsPlanetProof', cropFieldAdvice.isPlanetProof.toString())
      .set('IsGroundWaterPreservation', cropFieldAdvice.isGroundWaterPreservation.toString())
      .set('TemplateType', treatmentType.toString());

    if (cropFieldAdvice.province != null) {
      params = params.set('Province', cropFieldAdvice.province);
    }

    return this.http.get<Template>(`${this.baseUrlAdmin}template`, { params });
  }

  getLoggedInEmployee() {
    return this.http.get<Employee>(`${this.baseUrl}loggedinemployee`);
  }

  getFertilizationSettings(cropYear: number) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.get<{ fertilizationSettings: FertilizationSettingWithName[] }>(
        `${this.baseUrl}fertilization-settings/cropyear/${cropYear}`,
      );
    }
    return this.http.get<{ fertilizationSettings: FertilizationSettingWithName[] }>(
      `${this.baseUrl}fertilizationSetting/cropyear/${cropYear}`,
    );
  }

  saveFertilizationSetting(request: UpsertFertilizationSettingRequest) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post(`${this.baseUrl}fertilization-settings`, request);
    }
    return this.http.post(`${this.baseUrl}fertilizationsetting`, request);
  }

  updateGrasslandUsageForFertilizationSettings(request: UpdateGrasslandUsageRequest) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post(
        `${this.baseUrl}fertilization-settings/updateGrasslandUsageForCropFieldsByCustomerIdAndYear`,
        request,
      );
    }
    return this.http.post(
      `${this.baseUrl}fertilizationsetting/updateGrasslandUsageForCropFieldsByCustomerIdAndYear`,
      request,
    );
  }

  getCustomerFertilizerStock(cropYear: number) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http
        .get<CustomerFertilizerStock>(
          `${this.baseUrl}customer-fertilizer-stocks/cropyear/${cropYear}`,
        )
        .pipe(
          map((d) => d),
          catchError(() => of({ fertilizerStock: [] } as CustomerFertilizerStock)),
        );
    }
    return this.http
      .get<CustomerFertilizerStock>(`${this.baseUrl}customerfertilizerstock/cropYear/${cropYear}`)
      .pipe(
        map((d) => d),
        catchError(() => of({ fertilizerStock: [] } as CustomerFertilizerStock)),
      );
  }

  getNitrogenCalculationSettingToggles(adviceId: number) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.get<NitrogenCalculationSettingToggles>(
        `${this.baseUrl}fertilization-settings/nitrogensettingtoggles/adviceid/${adviceId}`,
      );
    }
    return this.http.get<NitrogenCalculationSettingToggles>(
      `${this.baseUrl}fertilizationsetting/nitrogensettingtoggles/adviceid/${adviceId}`,
    );
  }

  getNitrogenCalculationSettingTogglesByAdvices(adviceIds: number[]) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.get<{ settingToggles: NitrogenCalculationSettingToggles[] }>(
        `${this.baseUrl}fertilization-settings/nitrogensettingtoggles?${UrlHelpers.getParamString(
          'adviceIds',
          adviceIds,
        )}`,
      );
    }
    return this.http.get<{ settingToggles: NitrogenCalculationSettingToggles[] }>(
      `${this.baseUrl}fertilizationsetting/nitrogensettingtoggles?${UrlHelpers.getParamString(
        'adviceIds',
        adviceIds,
      )}`,
    );
  }

  getExecutedCropFieldTreatmentsByAdviceId(adviceId: number) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http
        .get<{
          executedTreatments: ExecutedTreatment[];
        }>(`${this.baseUrl}executedtreatment/advice/${adviceId}`)
        .pipe(map(({ executedTreatments }) => executedTreatments));
    }
    return this.http
      .get<{
        executedTreatments: ExecutedTreatment[];
      }>(`${this.baseUrl}executed-treatment/advice/${adviceId}`)
      .pipe(map(({ executedTreatments }) => executedTreatments));
  }

  getExecutedCropFieldTreatmentsByAdviceIds(adviceIds: number[]) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.get<{ executedTreatments: ExecutedTreatment[] }>(
        `${this.baseUrl}executedtreatment?${UrlHelpers.getParamString('adviceIds', adviceIds)}`,
      );
    }
    return this.http.get<{ executedTreatments: ExecutedTreatment[] }>(
      `${this.baseUrl}executed-treatment?${UrlHelpers.getParamString('adviceIds', adviceIds)}`,
    );
  }

  upsertTreatment(adviceId: number, treatment: Treatment) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post<{ ids: string[] }>(`${this.baseUrl}/${adviceId}/treatments`, {
        treatments: [treatment],
      });
    }
    return this.http.post<{ ids: string[] }>(`${this.baseUrl}advice/${adviceId}/treatments`, {
      treatments: [treatment],
    });
  }

  upsertTreatments(adviceId: number, treatments: Treatment[]) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post<{ ids: string[] }>(`${this.baseUrl}${adviceId}/treatments`, {
        treatments,
      });
    }
    return this.http.post<{ ids: string[] }>(`${this.baseUrl}advice/${adviceId}/treatments`, {
      treatments,
    });
  }

  upsertExecutedTreatment(executedTreatment: ExecutedTreatment) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post<{ id: number }>(`${this.baseUrl}executedtreatment`, executedTreatment);
    }
    return this.http.post<{ id: number }>(`${this.baseUrl}executed-treatment`, executedTreatment);
  }

  removeExecutedTreatment(id: number) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.delete(`${this.baseUrl}executedtreatment/${id}`);
    }
    return this.http.delete(`${this.baseUrl}executed-treatment/${id}`);
  }

  removeTreatment(adviceId: number, treatmentId: string) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.delete(`${this.baseUrl}${adviceId}/treatments/${treatmentId}`);
    }
    return this.http.delete(`${this.baseUrl}advice/${adviceId}/treatments/${treatmentId}`);
  }

  copyTreatments(
    sourceId: number,
    targetIds: number[],
    treatmentType: TreatmentType,
    copyExecutedTreatments: boolean,
    removeExistingTreatments: boolean,
  ) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post(`${this.baseUrl}${sourceId}/treatments/copy`, {
        destinationAdviceIds: targetIds,
        type: treatmentType,
        copyExecutedTreatments,
        removeExistingTreatments,
      });
    }
    return this.http.post(`${this.baseUrl}advice/${sourceId}/treatments/copy`, {
      destinationAdviceIds: targetIds,
      type: treatmentType,
      copyExecutedTreatments,
      removeExistingTreatments,
    });
  }

  copyByTreatments(sourceId: number, targetIds: number[], treatmentIds: string[]) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post(`${this.baseUrl}${sourceId}/treatments/copy-by-treatments`, {
        destinationAdviceIds: targetIds,
        treatmentIds,
      });
    }
    return this.http.post(`${this.baseUrl}advice/${sourceId}/treatments/copybytreatments`, {
      destinationAdviceIds: targetIds,
      treatmentIds,
    });
  }

  copyByExecutedTreatments(sourceId: number, targetIds: number[], executedTreatmentIds: number[]) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post(`${this.baseUrl}${sourceId}/treatments/copy-by-executed-treatments`, {
        destinationAdviceIds: targetIds,
        executedTreatmentIds,
      });
    }
    return this.http.post(`${this.baseUrl}advice/${sourceId}/treatments/copybyexecutedtreatments`, {
      destinationAdviceIds: targetIds,
      executedTreatmentIds,
    });
  }

  getCropFieldNutrientRequisitesByAdviceIds(cropFieldIds: number[], cropYear: number) {
    return this.http.get<RecommendedNutrientQuantityDataWithIds[]>(
      `${this.baseUrl}cropfieldnutrientrequisite/cropyear/${cropYear}?${UrlHelpers.getParamString(
        'cropFieldIds',
        cropFieldIds,
      )}`,
    );
  }

  clearAdvice(adviceId: number, treatmentType: TreatmentType) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.delete(`${this.baseUrl}${adviceId}/${treatmentType}`);
    }
    return this.http.delete(`${this.baseUrl}advice/${adviceId}/${treatmentType}`);
  }

  completeAdvice(adviceId: number, treatmentType: TreatmentType) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post(`${this.baseUrl}${adviceId}/${treatmentType}/complete`, {});
    }
    return this.http.post(`${this.baseUrl}advice/${adviceId}/${treatmentType}/complete`, {});
  }

  reopenAdvice(adviceId: number, treatmentType: TreatmentType) {
    if(this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post(`${this.baseUrl}${adviceId}/${treatmentType}/reopen`, {});
    }
    return this.http.post(`${this.baseUrl}advice/${adviceId}/${treatmentType}/reopen`, {});
  }

  getAdvicesByCustomerAndCropYear(
    customerId: number,
    cropYear: number,
  ): Observable<CropFieldAdvice[]> {
    return this.http
      .get<{
        cropFieldAdvices: CropFieldAdvice[];
      }>(`${this.baseUrl}cropfieldadvice/cropyear/${cropYear}/customerId/${customerId}`)
      .pipe(map((data) => data.cropFieldAdvices));
  }

  importAdviceFromCustomer(
    adviceId: number,
    sourceAdviceId: number,
    sourceCustomerId: number,
    treatmentType: TreatmentType,
  ) {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.post(`${this.baseUrl}${adviceId}/import`, {
        sourceAdviceId,
        sourceCustomerId,
        type: treatmentType,
      });
    }
    return this.http.post(`${this.baseUrl}advice/${adviceId}/import`, {
      sourceAdviceId,
      sourceCustomerId,
      type: treatmentType,
    });
  }

  getNonProductiveCrops(): Observable<CropData[]> {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http
        .get<{ crops: CropData[] }>(`${this.baseUrl}crops/non-productive`)
        .pipe(map((data) => data.crops));
    }
    return this.http
      .get<{ crops: CropData[] }>(`${this.baseUrl}crop/non-productive`)
      .pipe(map((data) => data.crops));
  }

  getFertilizationReport(
    year: number,
    executedTreatments: ExecutedTreatmentWithNitrogenEffectivity[],
    cropFieldNutrientRequisites: RecommendedNutrientQuantityDataWithIds[],
    adviceIds: number[],
  ): Observable<FertilizerReportItemApi[]> {
    return this.http
      .post<{ fertilizationDataCalculatorItems: FertilizerReportItemApi[] }>(
        `${this.baseUrlFieldReport}fertilization-data/report`,
        {
          year,
          executedTreatments,
          cropFieldNutrientRequisites,
          adviceIds,
        },
      )
      .pipe(map((data) => data.fertilizationDataCalculatorItems));
  }

  getFertilizationData(
    year: number,
    cropFieldNutrientRequisite: RecommendedNutrientQuantityDataWithIds,
    adviceId: number,
    editingTreatments: EditingTreatmentApi[],
  ): Observable<FertilizerReportItemApi> {
    return this.http
      .post<{ fertilizationDataCalculatorItem: FertilizerReportItemApi }>(
        `${this.baseUrlFieldReport}fertilization-data/editing-advice`,
        {
          year,
          cropFieldNutrientRequisite,
          adviceId,
          editingTreatments,
        },
      )
      .pipe(map((data) => data.fertilizationDataCalculatorItem));
  }

  resetCropFieldSyncStatus(year: number) {
    return this.http.delete(`${this.baseUrl}cropfieldsyncstatus/cropYear/${year}`);
  }

  getFertilizationBalanceSettings(year: number) {
    return this.http.get<FertilizationBalanceSettingsResponse>(
      `${this.baseUrlArableAdvice}advices/${year}/fertilization-balance-settings`,
    );
  }

  patchParticipation(adviceId: number, samplesBy: SamplesBy): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrlArableAdvice}advices/fertilization-balance-settings/${adviceId}/participate`,
      { samplesBy },
    );
  }

  patchUpdateSamplesTaker(adviceId: number, samplesBy: SamplesBy): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrlArableAdvice}advices/fertilization-balance-settings/${adviceId}/update-samples-taker`,
      { samplesBy },
    );
  }

  patchNotParticipating(adviceId: number): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrlArableAdvice}advices/fertilization-balance-settings/${adviceId}/not-participating`,
      {},
    );
  }

  patchCanceling(adviceId: number, isCanceling: boolean): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrlArableAdvice}advices/fertilization-balance-settings/${adviceId}/set-cancellation`,
      { isCanceling },
    );
  }

  patchExpectedCropYieldProduct(
    adviceId: number,
    cropYear: number,
    expectedCropYield: number,
  ): Observable<void> {
    if (this.env.FIELD_ADVICE_REBUILD_DONE) {
      return this.http.patch<void>(`${this.baseUrl}fertilization-settings/expected-crop-yield`, {
        adviceId,
        cropYear,
        expectedCropYield,
      });
    }
    return this.http.patch<void>(`${this.baseUrl}fertilizationSetting/expectedCropYieldProduct`, {
      adviceId,
      cropYear,
      expectedCropYield,
    });
  }

  postSendParticipationMail(year: number): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrlArableAdvice}advices/${year}/fertilization-balance-settings/send-participation`,
      {},
    );
  }

  postSendCancellationMail(year: number): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrlArableAdvice}advices/${year}/fertilization-balance-settings/send-cancellation`,
      {},
    );
  }

  patchSpecificReferenceBlock(
    adviceId: number,
    specificReferenceBlock: SpecificReferenceBlock,
  ): Observable<void> {
    const url = `${this.baseUrlArableAdvice}advices/fertilization-balance-settings/${adviceId}/set-specific-reference-block`;
    return this.http.patch<void>(url, specificReferenceBlock);
  }

  removeSpecificReferenceBlock(adviceId: number): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrlArableAdvice}advices/fertilization-balance-settings/${adviceId}/remove-specific-reference-block`,
      {},
    );
  }
}
